import { createUseStyles } from 'react-jss';
import { InlineButtonAlignments, InlineButtonElementTypes } from '../types/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { ControlTextColors } from '../../../../foundation/colors/controlTextColors/controlTextColors/index.js';
import { States } from '../../../../foundation/states/states/index.js';
import { ControlBorderColors } from '../../../../foundation/colors/controlBorderColors/controlBorderColors/index.js';
import { ControlBackgroundColors } from '../../../../foundation/colors/controlBackgroundColors/controlBackgroundColors/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';

const useSkinStyles = createUseStyles({
    '@keyframes rotate': {
        to: {
            transform: 'rotate(360deg)',
        },
    },
    '@keyframes arrowRotate': {
        to: {
            transform: 'rotate(180deg)',
        },
    },
    default: {
        backgroundColor: ({ isWithoutBackground }) => !isWithoutBackground && ControlBackgroundColors.default,
        borderColor: ({ isWithoutBorder }) => !isWithoutBorder && ControlBorderColors.default,
        color: ControlTextColors.default,
    },
    interactive: {
        [canHover]: {
            '&:hover': {
                backgroundColor: ControlBackgroundColors.action,
                borderColor: ({ isWithoutBorder }) => !isWithoutBorder && ControlBorderColors.action,
                color: ControlTextColors.action,
            },
        },
        [canTouch]: {
            '&:active': {
                backgroundColor: ControlBackgroundColors.action,
                borderColor: ({ isWithoutBorder }) => !isWithoutBorder && ControlBorderColors.action,
                color: ControlTextColors.action,
            },
        },
    },
    active: {
        backgroundColor: ControlBackgroundColors.active,
        borderColor: ({ isWithoutBorder }) => !isWithoutBorder && ControlBorderColors.active,
        color: ControlTextColors.active,
        '& .arrow': {
            transform: ({ isActive }) => isActive && 'rotate(180deg)',
        },
    },
    activeInteraction: {
        [canHover]: {
            '&:hover': {
                backgroundColor: ControlBackgroundColors.activeAction,
                borderColor: ({ isWithoutBorder }) => !isWithoutBorder && ControlBorderColors.activeAction,
                color: ControlTextColors.activeAction,
            },
        },
        [canTouch]: {
            '&:active': {
                backgroundColor: ControlBackgroundColors.activeAction,
                borderColor: ({ isWithoutBorder }) => !isWithoutBorder && ControlBorderColors.activeAction,
                color: ControlTextColors.activeAction,
            },
        },
    },
    disabled: {
        opacity: States.disabled,
        cursor: 'not-allowed',
    },
    loading: {
        color: 'transparent',
    },
    loadingIcon: {
        color: ControlTextColors.default,
        animation: `$rotate ${Timings.long} ${Easing.linear} infinite`,
    },
}, {
    name: 'inline-button',
});
const useLayoutStyles = createUseStyles({
    root: {
        '--icon-size': ({ sizes }) => sizes?.Default.value?.iconWidth && `${sizes?.Default.value?.iconWidth}px`,
        '--badge-size': ({ sizes }) => `${sizes?.Default.value?.badgeSize.rectangleWidth}px`,
        display: 'inline-flex',
        justifyContent: ({ centerIcon }) => centerIcon && 'center',
        verticalAlign: 'top',
        alignItems: 'center',
        flexShrink: 0,
        borderRadius: ({ sizes }) => sizes?.Default.value?.borderRadiusControlElement,
        border: ({ isWithoutBorder }) => isWithoutBorder && 'none',
        borderWidth: ({ isWithoutBorder, sizes }) => !isWithoutBorder && sizes?.Default.value?.borderWidth,
        borderStyle: ({ isWithoutBorder }) => !isWithoutBorder && 'solid',
        width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.Default.value?.blockHeight),
        height: ({ sizes }) => sizes?.Default.value?.blockHeight,
        padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.Default.value?.paddingVertical} ${sizes?.Default.value?.paddingHorizontal}`,
        columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.Default.value?.spaceBetween,
        fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.Default.value?.fontSize),
        backgroundColor: ({ isWithoutBackground, Tag }) => (isWithoutBackground && Tag === InlineButtonElementTypes.Button) && 'transparent',
        fontWeight: ({ sizes }) => sizes?.Default.value?.fontWeightButtonElements,
        lineHeight: ({ sizes }) => sizes?.Default.value?.lineHeight,
        transitionProperty: 'background-color, border, color',
        transitionTimingFunction: Easing.ease,
        transitionDuration: Timings.default,
        cursor: ({ Tag, isDisabled, isLoading }) => (isLoading ? 'default' : isDisabled ? 'not-allowed' : Tag !== InlineButtonElementTypes.Link && 'pointer'),
        [BreakPointsMedia.XXL]: {
            '--icon-size': ({ sizes }) => sizes?.XXL.value?.iconWidth && `${sizes?.XXL.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.XXL.value?.blockHeight),
            height: ({ sizes }) => sizes?.XXL.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XXL.value?.paddingVertical} ${sizes?.XXL.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.XXL.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.XXL.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XXL.value?.lineHeight,
        },
        [BreakPointsMedia.XL]: {
            '--icon-size': ({ sizes }) => sizes?.XL.value?.iconWidth && `${sizes?.XL.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.XL.value?.blockHeight),
            height: ({ sizes }) => sizes?.XL.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XL.value?.paddingVertical} ${sizes?.XL.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.XL.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.XL.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XL.value?.lineHeight,
        },
        [BreakPointsMedia.LG]: {
            '--icon-size': ({ sizes }) => sizes?.LG.value?.iconWidth && `${sizes?.LG.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.LG.value?.blockHeight),
            height: ({ sizes }) => sizes?.LG.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.LG.value?.paddingVertical} ${sizes?.LG.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.LG.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.LG.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.LG.value?.lineHeight,
        },
        [BreakPointsMedia.MD]: {
            '--icon-size': ({ sizes }) => sizes?.MD.value?.iconWidth && `${sizes?.MD.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.MD.value?.blockHeight),
            height: ({ sizes }) => sizes?.MD.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.MD.value?.paddingVertical} ${sizes?.MD.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.MD.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.MD.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.MD.value?.lineHeight,
        },
        [BreakPointsMedia.SM]: {
            '--icon-size': ({ sizes }) => sizes?.SM.value?.iconWidth && `${sizes?.SM.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.SM.value?.blockHeight),
            height: ({ sizes }) => sizes?.SM.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.SM.value?.paddingVertical} ${sizes?.SM.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.SM.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.SM.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.SM.value?.lineHeight,
        },
        [BreakPointsMedia.XS]: {
            '--icon-size': ({ sizes }) => sizes?.XS.value?.iconWidth && `${sizes?.XS.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.XS.value?.blockHeight),
            height: ({ sizes }) => sizes?.XS.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XS.value?.paddingVertical} ${sizes?.XS.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.XS.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.XS.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XS.value?.lineHeight,
        },
        [BreakPointsMedia.XXS]: {
            '--icon-size': ({ sizes }) => sizes?.XXS.value?.iconWidth && `${sizes?.XXS.value?.iconWidth}px`,
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon && sizes?.XXS.value?.blockHeight),
            height: ({ sizes }) => sizes?.XXS.value?.blockHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XXS.value?.paddingVertical} ${sizes?.XXS.value?.paddingHorizontal}`,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes?.XXS.value?.spaceBetween,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes?.XXS.value?.fontSize),
            lineHeight: ({ sizes }) => sizes?.XXS.value?.lineHeight,
        },
    },
    icon: {
        minWidth: 0,
        minHeight: 0,
        flexShrink: 0,
        display: 'inline-block',
        transitionProperty: 'transform',
        transitionTimingFunction: Easing.ease,
        transitionDuration: Timings.default,
        flex: '0 0 var(--icon-size)',
        width: 'var(--icon-size)',
        height: 'var(--icon-size)',
        '& > path': {
            '&:not([fill])': {
                fill: 'currentColor',
            },
        },
    },
    contentWrap: {
        display: 'grid',
        flexGrow: 1,
        height: ({ sizes }) => sizes?.Default.value?.contentHeight,
        padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.Default.value?.textPaddingVertical} ${sizes?.Default.value?.textPaddingHorizontal}`,
        [BreakPointsMedia.XXL]: {
            height: ({ sizes }) => sizes?.XXL.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XXL.value?.textPaddingVertical} ${sizes?.XXL.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.XL]: {
            height: ({ sizes }) => sizes?.XL.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XL.value?.textPaddingVertical} ${sizes?.XL.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.LG]: {
            height: ({ sizes }) => sizes?.LG.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.LG.value?.textPaddingVertical} ${sizes?.LG.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.MD]: {
            height: ({ sizes }) => sizes?.MD.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.MD.value?.textPaddingVertical} ${sizes?.MD.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.SM]: {
            height: ({ sizes }) => sizes?.SM.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.SM.value?.textPaddingVertical} ${sizes?.SM.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.XS]: {
            height: ({ sizes }) => sizes?.XS.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XS.value?.textPaddingVertical} ${sizes?.XS.value?.textPaddingHorizontal}`,
        },
        [BreakPointsMedia.XXS]: {
            height: ({ sizes }) => sizes?.XXS.value?.contentHeight,
            padding: ({ sizes, centerIcon }) => !centerIcon && `${sizes?.XXS.value?.textPaddingVertical} ${sizes?.XXS.value?.textPaddingHorizontal}`,
        },
    },
    content: {
        width: '100%',
        minWidth: 0,
        minHeight: 0,
        textAlign: ({ alignment }) => (alignment === InlineButtonAlignments.Center ? 'center' : alignment === InlineButtonAlignments.Start ? 'start' : 'end'),
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
    control: {
        display: 'none',
    },
    loading: {
        color: 'transparent',
        position: 'relative',
    },
    loadingIcon: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        margin: 'auto',
        pointerEvents: 'none',
    },
    badge: {
        minWidth: 0,
        minHeight: 0,
        flexShrink: 0,
    },
}, {
    name: 'inline-button',
});

export { useLayoutStyles, useSkinStyles };
